import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";
import { EChartsOption } from "echarts";

import { LimitChip, LimitDTO } from "src/app/models/limits/limitDTO";
import { LimitIntervalCodesEnum, LimitIntervalDTO } from "src/app/models/limits/limitIntervalDTO";
import { LimitMeasurementCodesEnum, LimitMeasurementDTO } from "src/app/models/limits/limitMeasurementDTO";
import {
  DefaultConfirmationDialogData,
  Dialog,
} from "src/app/models/dialog/dialog.model";

import { ConfirmationDialogComponent } from "../dialogs/confirmation-dialog/confirmation-dialog.component";

const LIMITS_FORM_PARAMS = {
  limits: "limits",
  code: "code",
  limitIntervalCode: "limitIntervalCode",
  renewalPeriod: "renewalPeriod",
  renewalOn: "renewalOn",
  limitMeasurementCode: "limitMeasurementCode",
  value: "value",
  currentlyOnHand: "currentlyOnHand",
  reservedLimit: "reservedLimit",
  currentlyUsed: "currentlyUsed",
};

@Component({
  selector: "limit-cards-list",
  templateUrl: "./limit-cards-list.component.html",
  styleUrls: ["./limit-cards-list.component.scss"],
})
export class LimitCardsListComponent {
  @Input() parentForm: FormGroup;
  @Input() set limits(limits: LimitDTO[]) {
    if (!limits) {
      return;
    }
    this.setLimitsDataView(limits);
    this.populateLimits();
    this.addLimitsChips();
  }
  @Input() limitIntervals: LimitIntervalDTO[];
  @Input() limitMeasurements: LimitMeasurementDTO[];
  @Input() dateTimeFormat: string;
  @Input() duplication: boolean;

  @Output() limitReseted: EventEmitter<number> = new EventEmitter<number>();

  _limits: LimitDTO[] = [];
  chipsList: LimitChip[] = [];

  limitIntervalCodesEnum = LimitIntervalCodesEnum;
  limitMeasurementCodesEnum = LimitMeasurementCodesEnum;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    public dialog: MatDialog
  ) {}

  populateLimits() {
    const limits = this.getFormArray(LIMITS_FORM_PARAMS.limits);
    this.clearFormArray(limits);
    this._limits.forEach((limit) => {
      const limitFormGroup = this.createLimitFormGroup(limit);
      limits.push(limitFormGroup);
      this.setValueValidators(limitFormGroup);
      this.onLimitIntervalCodeChanges(limitFormGroup);
    });
  }

  getFormArray(formParam: string): FormArray {
    return this.parentForm.get(formParam) as FormArray;
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  createLimitFormGroup(limit?: LimitDTO): FormGroup {
    const {
      code,
      limitIntervalCode,
      renewalPeriod,
      limitMeasurementCode,
      value,
      currentlyUsed,
      reservedLimit,
      currentlyOnHand,
      renewalOn,
    } = limit || {};
    return this.fb.group({
      [LIMITS_FORM_PARAMS.code]: [code ?? null],
      [LIMITS_FORM_PARAMS.limitIntervalCode]: [
        limitIntervalCode ?? null,
        [Validators.required],
      ],
      [LIMITS_FORM_PARAMS.renewalPeriod]: [
        {
          value: renewalPeriod ?? null,
          disabled: limitIntervalCode === LimitIntervalCodesEnum.SUPPLY,
        },
        [Validators.required, Validators.min(0)],
      ],
      [LIMITS_FORM_PARAMS.renewalOn]: [
        { value: renewalOn ?? null, disabled: limitIntervalCode === LimitIntervalCodesEnum.SUPPLY },
        [Validators.required],
      ],
      [LIMITS_FORM_PARAMS.limitMeasurementCode]: [
        limitMeasurementCode ?? null,
        [Validators.required],
      ],
      [LIMITS_FORM_PARAMS.value]: [
        value ?? null,
        [Validators.required, Validators.min(0)],
      ],
      [LIMITS_FORM_PARAMS.currentlyOnHand]: [currentlyOnHand ?? null],
      [LIMITS_FORM_PARAMS.reservedLimit]: [reservedLimit ?? null],
      [LIMITS_FORM_PARAMS.currentlyUsed]: [currentlyUsed ?? null],
    });
  }

  setValueValidators(form: FormGroup): void {
    const limitIntervalCode = form.get(LIMITS_FORM_PARAMS.limitIntervalCode).value;
    if (limitIntervalCode === LimitIntervalCodesEnum.SUPPLY) {
      this.setSupplyValueValidators(form);
    } else {
      this.setDefaultValueValidators(form);
    }
  }

  onLimitIntervalCodeChanges(form: FormGroup): void {
    form
      .get(LIMITS_FORM_PARAMS.limitIntervalCode)
      .valueChanges.subscribe((limitIntervalCode) => {
        if (limitIntervalCode === LimitIntervalCodesEnum.SUPPLY) {
          this.setSupplyValueValidators(form);
        } else {
          this.setDefaultValueValidators(form);
        }
      });
  }

  setSupplyValueValidators(form: FormGroup): void {
    this.disableFormField(form, LIMITS_FORM_PARAMS.renewalPeriod);
    this.disableFormField(form, LIMITS_FORM_PARAMS.renewalOn);
    form.get(LIMITS_FORM_PARAMS.renewalPeriod).setValue(1);
    form.get(LIMITS_FORM_PARAMS.renewalOn).setValue(null);
    form.get(LIMITS_FORM_PARAMS.value).addValidators([
      Validators.required, 
      Validators.min(0), 
      Validators.max(9999.99),
    ]);
    form.get(LIMITS_FORM_PARAMS.value).updateValueAndValidity();
    if (form.get(LIMITS_FORM_PARAMS.value).value) {
      form.get(LIMITS_FORM_PARAMS.value).markAsTouched();
    }
    if (
      form.get(LIMITS_FORM_PARAMS.limitMeasurementCode).value &&
      form.get(LIMITS_FORM_PARAMS.limitMeasurementCode).value === LimitMeasurementCodesEnum.NUMBER_REFUELS
    ) {
      form.get(LIMITS_FORM_PARAMS.limitMeasurementCode).setValue(null);
    }
  }

  setDefaultValueValidators(form: FormGroup): void {
    this.enableFormField(form, LIMITS_FORM_PARAMS.renewalPeriod);
    this.enableFormField(form, LIMITS_FORM_PARAMS.renewalOn);
    form.get(LIMITS_FORM_PARAMS.value).setValidators([
      Validators.required,
      Validators.min(0),
      Validators.max(99999999),
    ]);
    form.get(LIMITS_FORM_PARAMS.value).updateValueAndValidity();
    if (form.get(LIMITS_FORM_PARAMS.value).value) {
      form.get(LIMITS_FORM_PARAMS.value).markAsTouched();
    }
  }

  disableFormField(form: FormGroup, formParam: string) {
    form.get(formParam).clearValidators();
    form.get(formParam).updateValueAndValidity();
    form.get(formParam).disable();
  }

  enableFormField(form: FormGroup, formParam: string) {
    form.get(formParam).setValidators([Validators.required]);
    form.get(formParam).enable();
  }

  addLimit() {
    const limits = this.getFormArray(LIMITS_FORM_PARAMS.limits);
    const limitFormGroup = this.createLimitFormGroup();
    limits.push(limitFormGroup);
    this.onLimitIntervalCodeChanges(limitFormGroup);
    this.parentForm.markAsDirty();
  }

  openRemoveLimitDialog(index: number) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.removeLimit(index);
      }
    });
  }

  removeLimit(index: number) {
    const limits = this.getFormArray(LIMITS_FORM_PARAMS.limits);
    limits.removeAt(index);
    if (this._limits) {
      this._limits = this._limits.filter(
        (limit, limitIndex) => limitIndex !== index
      );
    }
    this.parentForm.markAsDirty();
  }

  setChartOptions(index: number): EChartsOption {
    return {
      tooltip: {
        trigger: "item",
        textStyle: {
          fontSize: 12,
        },
        confine: true,
      },
      series: [
        {
          name: this.translate.instant("Limits.limitProgress"),
          type: "pie",
          radius: ["40%", "70%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 1,
          },
          label: {
            show: false,
            position: "center",
          },
          labelLine: {
            show: false,
          },
          data:
            !this.duplication && this._limits && index < this._limits.length && 
            (this._limits[index].currentlyUsed > 0 || this._limits[index].reservedLimit > 0 || this._limits[index].currentlyOnHand > 0)
              ? [
                  {
                    value:
                      this._limits[index].currentlyUsed > 0
                        ? this._limits[index].currentlyUsed
                        : 0,
                    name: this.translate.instant("Limits.used"),
                    itemStyle: { color: "#858585" },
                  },
                  {
                    value:
                      this._limits[index].reservedLimit > 0
                        ? this._limits[index].reservedLimit
                        : 0,
                    name: this.translate.instant("Limits.reserved"),
                    itemStyle: { color: "#ffc803" },
                  },
                  {
                    value:
                      this._limits[index].currentlyOnHand > 0
                        ? this._limits[index].currentlyOnHand
                        : 0,
                    name: this.translate.instant("Limits.available"),
                    itemStyle: { color: "#3aab40" },
                  },
                ]
              : [{ value: 0, itemStyle: { color: "gray" } }],
        },
      ],
    };
  }

  addLimitsChips() {
    this._limits = this._limits.map((limit) => {
      return {
        ...limit,
        chipsList: this.setChipsList(limit),
      }
    })
  }

  setLimitsDataView(limits: LimitDTO[]) {
    this._limits = limits.map((limit) => ({ ...limit, showChipsList: false }));
  }

  setChipsList(limit) {
    const { currentlyOnHand, currentlyUsed, reservedLimit } = limit;
    const currentlyOnHandStyle = this.setBasicChipStyle("currentlyOnHand");
    const currentlyUsedStyle = this.setBasicChipStyle("currentlyUsed");
    const reservedLimitStyle = this.setBasicChipStyle("reservedLimit");

    return [
      {
        value: !this.duplication ? currentlyOnHand ?? 0 : 0,
        style: currentlyOnHandStyle.style,
        translation: this.translate.instant("Contracts.currentlyOnHand"),
      },
      {
        value: !this.duplication ? currentlyUsed ?? 0 : 0,
        style: currentlyUsedStyle.style,
        translation: this.translate.instant("Contracts.currentlyUsed"),
      },
      {
        value: !this.duplication ? reservedLimit ?? 0 : 0,
        style: reservedLimitStyle.style,
        translation: this.translate.instant("Contracts.reservedLimit"),
      }
    ];
  }

  changeDataView(limit: LimitDTO) {
    limit.showChipsList = !limit.showChipsList;
  }

  setBasicChipStyle(value: string) {
    switch (value) {
      case "currentlyOnHand":
        return {
          style: "background-color: #3aab40; color: white; width:100%",
        };
      case "currentlyUsed":
        return {
          style: "background-color: #858585; color: white; width:100%",
        };
      case "reservedLimit":
        return {
          style: "background-color: #ffc803; color: white; width:100%",
        };
    }
  }

  resetContractLimit(index: number) {
    this.limitReseted.emit(this._limits[index].code);
  }
}
